import type { Client } from '@snapshots/transport';
import type { Identity } from './types';

/** A sign-in that came back from Accounts without a session: the service's code, and where it was going */
export interface Failure {
	code: string;
	to: string;
}

/**
 * Who is here, according to the service. Signing in happens at Accounts through the service's
 * hand-off, which this application only navigates to; signing out asks the service to end the session
 * at Accounts, because forgetting a cookie is not signing out.
 */
export class Session {
	/** The page a failed hand-off lands on, with `failed=<code>` and `to=<destination>` */
	static RETURN = '/signin';

	#client: Client;

	constructor(client: Client) {
		this.#client = client;
	}

	/** A path of this application, or `/`: never another origin (`//host`) and never the sign-in page itself */
	static path(value: string | null | undefined): string {
		if (!value || !value.startsWith('/') || value.startsWith('//')) return '/';
		return value === Session.RETURN || value.startsWith(`${Session.RETURN}?`) ? '/' : value;
	}

	/** Where signing in from this address should come back to: the address itself, or what a failed return names */
	static destination(pathname: string, search: string): string {
		if (pathname === Session.RETURN) return Session.path(new URLSearchParams(search).get('to'));
		return Session.path(`${pathname}${search}`);
	}

	/** What a failed return says, when this address is one */
	static failure(pathname: string, search: string): Failure | undefined {
		if (pathname !== Session.RETURN) return undefined;
		const parameters = new URLSearchParams(search);
		const code = parameters.get('failed');
		return code ? { code, to: Session.path(parameters.get('to')) } : undefined;
	}

	identity(): Promise<Identity> {
		return this.#client.get<Identity>('/v1/session');
	}

	/**
	 * Where the browser goes to sign in, returning to `to` afterwards; with a `mode`, the hand-off
	 * `Session` of `@beyond-js/ui` runs: `silent` in a hidden frame, `window` in the sign-in window (both
	 * end on the landing) or `tab` in this tab
	 */
	signin(to = '/', mode?: 'silent' | 'window' | 'tab'): string {
		const query = mode ? `mode=${mode}&next=${encodeURIComponent(to)}` : `to=${encodeURIComponent(to)}`;
		return `${this.#client.origin}/v1/auth/start?${query}`;
	}

	/** @returns Whether Accounts confirmed that the session ended. @throws Refusal when the service did not sign out */
	async leave(): Promise<boolean> {
		const { ended } = await this.#client.post<{ ended: boolean }>('/v1/auth/signout');
		return ended;
	}
}
